import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase, isConfigured, getProfile, getFloorPlans, getFloorPlanById, createFloorPlan, updateFloorPlan, deleteFloorPlan } from './services/supabase';
import type { FloorPlan, UserProfile } from './types';
import Header from './components/Header';
import Login from './components/Login';
import ProjectsPage from './components/ProjectsPage';
import GridRevealBackground from './components/GridRevealBackground';
import Footer from './components/Footer';
import { FLOORPLAN_TAGLINE, FLOORPLAN_FEATURES } from './constants';
import FloorPlanEditor from './components/FloorPlanEditor';
import type { SaveStatus } from './components/Panels/StatusBar';
import { clearFloorPlanDraft, writeFloorPlanDraft } from './draftStorage';
import CreditsModal from './components/CreditsModal';
import { Loader2, Sparkles } from 'lucide-react';

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginMode, setLoginMode] = useState<'login' | 'signup'>('signup');
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([]);
  const [currentFloorPlan, setCurrentFloorPlan] = useState<FloorPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');

  const path = window.location.pathname;
  const editorMatch = path.match(/^\/editor\/([a-f0-9-]+)$/);
  const editorId = editorMatch?.[1];

  useEffect(() => {
    if (!isConfigured) {
      setLoading(false);
      return;
    }
    const isAuthCallback = window.location.pathname === '/auth/callback';
    const hash = window.location.hash;
    if (hash.includes('sso_access_token')) {
      const params = new URLSearchParams(hash.substring(1));
      const at = params.get('sso_access_token');
      const rt = params.get('sso_refresh_token');
      window.history.replaceState({}, '', window.location.pathname);
      if (at && rt) {
        supabase.auth.setSession({ access_token: at, refresh_token: rt }).then(({ data }) => {
          if (data.session) {
            setSession(data.session);
            if (isAuthCallback) window.history.replaceState({}, '', '/');
          }
        });
        return;
      }
    }
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setLoading(false);
      if (s && isAuthCallback) window.history.replaceState({}, '', '/');
    });
  }, []);

  useEffect(() => {
    if (!isConfigured) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (s && window.location.pathname === '/auth/callback') {
        window.history.replaceState({}, '', '/');
      }
      if (!s) setUserProfile(null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session?.user?.id) return;
    getProfile(session.user.id).then((p) => {
      setUserProfile(p ?? { id: session.user.id, email: session.user.email ?? '', credits: 0 });
    });
  }, [session?.user?.id]);

  useEffect(() => {
    if (!session?.user?.id || !isConfigured) return;
    getFloorPlans(session.user.id).then(setFloorPlans);
  }, [session?.user?.id]);

  const handleNewFloorPlan = useCallback(async () => {
    if (!session?.user?.id) return;
    const fp = await createFloorPlan(session.user.id);
    if (fp) {
      setFloorPlans((prev) => [fp, ...prev]);
      setCurrentFloorPlan(fp);
      window.history.pushState({}, '', `/editor/${fp.id}`);
    }
  }, [session?.user?.id]);

  const handleOpenFloorPlan = useCallback((fp: FloorPlan) => {
    setCurrentFloorPlan(fp);
    window.history.pushState({}, '', `/editor/${fp.id}`);
  }, []);

  const saveRef = useRef<{
    fp: FloorPlan | null;
    userId: string | null;
    timer: ReturnType<typeof setTimeout> | null;
  }>({ fp: null, userId: null, timer: null });

  const runSave = useCallback(async (fp: FloorPlan, userId: string) => {
    setSaveStatus('saving');
    const result = await updateFloorPlan(fp.id, userId, { name: fp.name, canvas_data: fp.canvas_data });
    if (saveRef.current.fp) return;
    setSaveStatus(result ? 'saved' : 'local');
  }, []);

  const flushSave = useCallback(() => {
    const pending = saveRef.current;
    if (pending.timer) clearTimeout(pending.timer);
    pending.timer = null;
    const fp = pending.fp;
    const userId = pending.userId;
    pending.fp = null;
    if (fp && userId) void runSave(fp, userId);
  }, [runSave]);

  const flushSaveRef = useRef(flushSave);
  flushSaveRef.current = flushSave;

  useEffect(() => {
    const flush = () => flushSaveRef.current();
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, []);

  const handleFloorPlanUpdate = useCallback((fp: FloorPlan) => {
    setCurrentFloorPlan(fp);
    setFloorPlans((prev) => prev.map((p) => (p.id === fp.id ? fp : p)));
    writeFloorPlanDraft(fp.id, {
      name: fp.name,
      canvas_data: fp.canvas_data,
      savedAt: new Date().toISOString(),
    });
    setSaveStatus('local');
    if (!session?.user?.id) return;
    if (saveRef.current.timer) clearTimeout(saveRef.current.timer);
    saveRef.current.fp = fp;
    saveRef.current.userId = session.user.id;
    saveRef.current.timer = setTimeout(() => {
      const pending = saveRef.current.fp;
      const userId = saveRef.current.userId;
      saveRef.current.timer = null;
      saveRef.current.fp = null;
      if (pending && userId) void runSave(pending, userId);
    }, 800);
  }, [session?.user?.id, runSave]);

  const handleBackToProjects = useCallback(() => {
    flushSave();
    setCurrentFloorPlan(null);
    window.history.pushState({}, '', '/');
    if (session?.user?.id) getFloorPlans(session.user.id).then(setFloorPlans);
  }, [session?.user?.id, flushSave]);

  const handleFloorPlanDelete = useCallback(async (fp: FloorPlan) => {
    if (!session?.user?.id || !confirm('Delete this floor plan?')) return;
    const ok = await deleteFloorPlan(fp.id, session.user.id);
    if (ok) {
      clearFloorPlanDraft(fp.id);
      setFloorPlans((prev) => prev.filter((p) => p.id !== fp.id));
      if (currentFloorPlan?.id === fp.id) {
        setCurrentFloorPlan(null);
        window.history.pushState({}, '', '/');
      }
    }
  }, [session?.user?.id, currentFloorPlan?.id]);

  const handleCreditsChange = useCallback((delta: number) => {
    setUserProfile((p) => (p ? { ...p, credits: p.credits + delta } : p));
  }, []);

  useEffect(() => {
    const onPopState = () => {
      const p = window.location.pathname;
      const m = p.match(/^\/editor\/([a-f0-9-]+)$/);
      if (m) {
        const id = m[1];
        const fp = floorPlans.find((f) => f.id === id);
        setCurrentFloorPlan(fp ?? null);
      } else {
        setCurrentFloorPlan(null);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [floorPlans]);

  useEffect(() => {
    if (!editorId || !session?.user?.id) return;
    const fp = floorPlans.find((f) => f.id === editorId);
    if (fp) {
      setCurrentFloorPlan(fp);
      return;
    }
    getFloorPlanById(editorId, session.user.id).then((loaded) => {
      if (loaded) {
        setCurrentFloorPlan(loaded);
        setFloorPlans((prev) => (prev.some((p) => p.id === loaded.id) ? prev : [loaded, ...prev]));
      }
    });
  }, [editorId, session?.user?.id, floorPlans]);

  if (path === '/auth/callback') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#111118' }}>
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={32} className="animate-spin" style={{ color: '#FF4C4C' }} />
          <p style={{ color: '#8888aa' }}>Signing you in...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <GridRevealBackground />
        <Header user={null} onLogin={() => { setLoginMode('login'); setShowLoginModal(true); }} onLogout={() => {}} onSignUp={() => { setLoginMode('signup'); setShowLoginModal(true); }} />
        <main className="flex-1 flex flex-col items-center justify-center px-4 py-12 sm:py-16 relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <div className="flex justify-center mb-8">
              <img src="/Floor Plan Logo.png" alt="Floor Plan" className="h-16 sm:h-20 w-auto object-contain" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Floor Plan</h1>
            <p className="text-lg text-slate-300 mb-10">{FLOORPLAN_TAGLINE}</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => { setLoginMode('signup'); setShowLoginModal(true); }}
                className="group flex items-center gap-3 px-8 py-4 text-white rounded-2xl font-semibold text-lg transition-all hover:opacity-90 shadow-lg"
                style={{ backgroundColor: '#FF4C4C', boxShadow: '0 10px 40px -10px rgba(255,76,76,0.4)' }}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 group-hover:rotate-[360deg] transition-transform duration-700">
                  <Sparkles size={18} strokeWidth={2} />
                </span>
                Get Started Free
              </button>
              <button
                onClick={() => { setLoginMode('login'); setShowLoginModal(true); }}
                className="px-8 py-4 rounded-2xl font-semibold text-lg transition-colors border text-slate-200 hover:bg-[#8E0000]/20"
                style={{ backgroundColor: '#252532', borderColor: '#2e2e40' }}
              >
                Sign In
              </button>
            </div>
            <p className="text-sm text-slate-400 mt-4">10 free credits on signup. No credit card required.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-16 max-w-4xl mx-auto w-full">
            {FLOORPLAN_FEATURES.map((f) => (
              <div
                key={f.title}
                className="p-5 rounded-2xl border text-left transition-colors hover:border-[#FF4C4C]/50"
                style={{ backgroundColor: 'rgba(28,28,40,0.8)', borderColor: '#2e2e40', color: '#f0f0f5' }}
              >
                <h3 className="font-semibold mb-1">{f.title}</h3>
                <p className="text-sm text-slate-400">{f.description}</p>
              </div>
            ))}
          </div>
        </main>
        <Footer />
        {showLoginModal && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-lg">
              <Login onClose={() => setShowLoginModal(false)} initialMode={loginMode} />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#111118' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: '#FF4C4C' }} />
      </div>
    );
  }

  if (session && userProfile && currentFloorPlan) {
    return (
      <>
        <FloorPlanEditor
          floorPlan={currentFloorPlan}
          user={userProfile}
          onUpdate={handleFloorPlanUpdate}
          onDelete={() => handleFloorPlanDelete(currentFloorPlan)}
          onBackToProjects={handleBackToProjects}
          onCreditsChange={handleCreditsChange}
          saveStatus={saveStatus}
        />
        {showCreditsModal && <CreditsModal user={userProfile} onClose={() => setShowCreditsModal(false)} />}
      </>
    );
  }

  return (
    <>
      <ProjectsPage
        floorPlans={floorPlans}
        onNewFloorPlan={handleNewFloorPlan}
        onOpenFloorPlan={handleOpenFloorPlan}
        onDeleteFloorPlan={session?.user?.id ? handleFloorPlanDelete : undefined}
        header={
          <Header
            user={userProfile}
            onLogin={() => {}}
            onLogout={async () => { await supabase.auth.signOut(); setSession(null); setUserProfile(null); setFloorPlans([]); }}
            onOpenCredits={() => setShowCreditsModal(true)}
          />
        }
      />
      {showCreditsModal && userProfile && <CreditsModal user={userProfile} onClose={() => setShowCreditsModal(false)} />}
    </>
  );
}

export default App;
